import { useState, useEffect } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialogue.tsx';
import { Button } from '../ui/button.tsx';

import {
  useCreateSubject,
  useUpdateSubject,
} from '../../api/subject/subjectQueries';

// MongoDB ObjectID validation helper function
const isValidObjectId = (id) => {
  // MongoDB ObjectID is a 24 character hex string
  const objectIdPattern = /^[0-9a-fA-F]{24}$/;
  return objectIdPattern.test(id);
};

// Simplified initialFormState with only required fields
const initialFormState = {
  subjectName: '',
  departmentId: '',
};

export default function SubjectModal({
  isOpen,
  onClose,
  departments = [],
  subjectToEdit = null,
}) {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  const { mutate: createSubject, isPending: isCreating } = useCreateSubject();
  const { mutate: updateSubject, isPending: isUpdating } = useUpdateSubject();

  const isEditMode = !!subjectToEdit;
  const isPending = isCreating || isUpdating;

  // Set form data when in edit mode
  useEffect(() => {
    if (isEditMode && subjectToEdit) {
      setFormData({
        subjectName: subjectToEdit.subjectName || '',
        departmentId: subjectToEdit.departmentId || '',
      });
    } else {
      // Reset form when switching back to create mode
      setFormData(initialFormState);
    }
  }, [subjectToEdit, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.subjectName.trim()) {
      newErrors.subjectName = 'Subject name is required';
    }

    if (!formData.departmentId) {
      newErrors.departmentId = 'Department is required';
    } else if (!isValidObjectId(formData.departmentId)) {
      newErrors.departmentId = 'Invalid department ID format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (isEditMode) {
      // For update, use just the required fields
      const updatePayload = {
        subjectId: subjectToEdit.id,
        data: {
          subjectName: formData.subjectName,
          // Only include departmentId if it's a valid MongoDB ObjectId
          ...(formData.departmentId &&
            isValidObjectId(formData.departmentId) && {
              departmentId: formData.departmentId,
            }),
        },
      };

      updateSubject(updatePayload, {
        onSuccess: () => {
          onClose();
        },
        onError: (error) => {
          // Display specific error for ObjectID issues
          if (error.message && error.message.includes('ObjectID')) {
            setErrors((prev) => ({
              ...prev,
              departmentId:
                'Invalid department ID format. Please select a valid department.',
            }));
          }
        },
      });
    } else {
      // For create, only send the required fields according to the API contract
      const createPayload = {
        subjectName: formData.subjectName,
        // Only include departmentId if it's a valid MongoDB ObjectId
        ...(formData.departmentId &&
          isValidObjectId(formData.departmentId) && {
            departmentId: formData.departmentId,
          }),
      };

      createSubject(createPayload, {
        onSuccess: () => {
          setFormData(initialFormState);
          onClose();
        },
        onError: (error) => {
          // Display specific error for ObjectID issues
          if (error.message && error.message.includes('ObjectID')) {
            setErrors((prev) => ({
              ...prev,
              departmentId:
                'Invalid department ID format. Please select a valid department.',
            }));
          }
        },
      });
    }
  };

  const resetForm = () => {
    setFormData(
      isEditMode && subjectToEdit
        ? {
            subjectName: subjectToEdit.subjectName || '',
            departmentId: subjectToEdit.departmentId || '',
          }
        : initialFormState
    );
    setErrors({});
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          resetForm();
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Edit Subject' : 'Add New Subject'}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label
                htmlFor="subjectName"
                className="text-right text-sm font-medium"
              >
                Subject Name
              </label>
              <div className="col-span-3">
                <input
                  id="subjectName"
                  name="subjectName"
                  value={formData.subjectName}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${
                    errors.subjectName ? 'border-red-500' : 'border-gray-300'
                  } px-3 py-2`}
                />
                {errors.subjectName && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.subjectName}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <label
                htmlFor="departmentId"
                className="text-right text-sm font-medium"
              >
                Department
              </label>
              <div className="col-span-3">
                <select
                  id="departmentId"
                  name="departmentId"
                  value={formData.departmentId}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${
                    errors.departmentId ? 'border-red-500' : 'border-gray-300'
                  } px-3 py-2`}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => {
                    // Handle different department object structures
                    const deptId = dept.id || dept.deptId;
                    const deptName = dept.name || dept.departmentName;

                    return deptId && isValidObjectId(deptId) ? (
                      <option key={deptId} value={deptId}>
                        {deptName}
                      </option>
                    ) : null;
                  })}
                </select>
                {errors.departmentId && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.departmentId}
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? isEditMode
                  ? 'Updating...'
                  : 'Creating...'
                : isEditMode
                ? 'Update Subject'
                : 'Create Subject'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
